import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, MapPin, Users, Clock, AlertTriangle } from 'lucide-react';
import Header from '../components/Header';
import MainTabsNav from '../components/MainTabsNav';
import Footer from '../components/Footer';
import AdminSidebar from '../components/AdminSidebar';
import { useAuthStore } from '../stores/authStore';

interface EnrollmentWithDetails {
  id: string;
  user_id: string;
  class_id: string;
  status: 'ACTIVE' | 'CANCELLED';
  created_at: string;
  updated_at: string;
  class?: {
    id: string;
    title: string;
    thumbnail_url: string | null;
    start_date: string;
    end_date: string;
    status: string;
    provider_id: string;
  };
}

export default function MyEnrollmentsPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [enrollments, setEnrollments] = useState<EnrollmentWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [cancelingId, setCancelingId] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchEnrollments();
  }, [user, navigate]);

  const fetchEnrollments = async () => {
    try {
      const response = await fetch('/api/v1/enrollments', {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch enrollments');
      }

      const data = await response.json();
      setEnrollments(data.enrollments || []);
    } catch (error) {
      console.error('Failed to fetch enrollments:', error);
      alert('수강 신청 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEnrollment = async (enrollmentId: string, classTitle: string, classStatus: string) => {
    // 진행중이거나 종료된 수업은 취소 불가 (모집예정은 취소 허용)
    if (classStatus !== 'RECRUITING' && classStatus !== 'SCHEDULED') {
      alert('진행중이거나 종료된 수업은 취소할 수 없습니다.');
      return;
    }

    const confirmed = confirm(`"${classTitle}" 수업의 수강 신청을 취소하시겠습니까?`);
    if (!confirmed) return;

    setCancelingId(enrollmentId);

    try {
      const response = await fetch(`/api/v1/enrollments/${enrollmentId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || '수강 신청 취소에 실패했습니다.');
      }

      alert('수강 신청이 취소되었습니다.');
      fetchEnrollments(); // 목록 새로고침
    } catch (error: any) {
      console.error('Failed to cancel enrollment:', error);
      alert(error.message || '수강 신청 취소에 실패했습니다.');
    } finally {
      setCancelingId(null);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'SCHEDULED':
        return <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded">모집예정</span>;
      case 'RECRUITING':
        return <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded">모집중</span>;
      case 'IN_PROGRESS':
        return <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded">진행중</span>;
      case 'COMPLETED':
        return <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded">종료</span>;
      case 'CLOSED':
        return <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded">마감</span>;
      default:
        return null;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <MainTabsNav className="mt-16" />
        <div className="flex flex-1 max-w-[1440px] mx-auto w-full">
          <AdminSidebar />
          <main className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600">로딩 중...</p>
            </div>
          </main>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <MainTabsNav className="mt-16" />
      <div className="flex flex-1 max-w-[1440px] mx-auto w-full">
        <AdminSidebar />
        <main className="flex-1 min-w-0 p-6 sm:px-10 lg:px-16">
          <div className="max-w-6xl mx-auto">
        {/* 헤더 */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">내 수강 신청 관리</h1>
          <p className="text-gray-600">신청한 수업 목록을 확인하고 관리할 수 있습니다.</p>
        </div>

        {/* 수강 신청 목록 */}
        {enrollments.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">신청한 수업이 없습니다</h3>
            <p className="text-gray-600 mb-6">관심있는 수업을 찾아 수강 신청해보세요!</p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
            >
              수업 둘러보기
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {enrollments.map((enrollment) => {
              const classData = enrollment.class;
              if (!classData) return null;

              const canCancel = classData.status === 'RECRUITING' || classData.status === 'SCHEDULED';

              return (
                <div
                  key={enrollment.id}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
                >
                  <div className="flex flex-col md:flex-row">
                    {/* 썸네일 */}
                    <div
                      className="w-full md:w-64 h-48 md:h-auto bg-gray-200 cursor-pointer"
                      onClick={() => navigate(`/classes/${classData.id}`)}
                    >
                      {classData.thumbnail_url ? (
                        <img
                          src={classData.thumbnail_url}
                          alt={classData.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                          이미지 없음
                        </div>
                      )}
                    </div>

                    {/* 정보 */}
                    <div className="flex-1 min-w-0 p-6 sm:px-10 lg:px-16">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            {getStatusBadge(classData.status)}
                            {!canCancel && (
                              <span className="flex items-center gap-1 text-xs text-gray-500">
                                <AlertTriangle className="w-3 h-3" />
                                취소 불가
                              </span>
                            )}
                          </div>
                          <h3
                            className="text-xl font-bold text-gray-900 mb-2 cursor-pointer hover:text-primary transition-colors"
                            onClick={() => navigate(`/classes/${classData.id}`)}
                          >
                            {classData.title}
                          </h3>
                          <div className="flex items-center gap-4 text-sm text-gray-600">
                            <div className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              <span>
                                {formatDate(classData.start_date)} - {formatDate(classData.end_date)}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* 취소 버튼 */}
                        <button
                          onClick={() => handleCancelEnrollment(enrollment.id, classData.title, classData.status)}
                          disabled={!canCancel || cancelingId === enrollment.id}
                          className={`ml-4 px-4 py-2 rounded-lg transition-colors ${
                            canCancel
                              ? 'bg-red-500 text-white hover:bg-red-600'
                              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          } ${cancelingId === enrollment.id ? 'opacity-50 cursor-wait' : ''}`}
                        >
                          {cancelingId === enrollment.id ? '취소 중...' : '수강 취소'}
                        </button>
                      </div>

                      {/* 신청 정보 */}
                      <div className="pt-4 border-t border-gray-100">
                        <p className="text-sm text-gray-500">
                          신청일: {formatDate(enrollment.created_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
          </div>
        </main>
      </div>
      <Footer />
    </div>
  );
}
